import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { mainTheme, adminTheme } from './theme';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import AdminLoginGate from './layouts/AdminLoginGate';
import MainPage from './pages/MainPage';
import EventListPage from './pages/EventListPage';
import ChurchListPage from './pages/ChurchListPage';
import ReceiptPage from './pages/ReceiptPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import EventManagePage from './pages/admin/EventManagePage';
import ChurchManagePage from './pages/admin/ChurchManagePage';
import ReceiptManagePage from './pages/admin/ReceiptManagePage';
import EventReceiptManagePage from './pages/admin/EventReceiptManagePage';
import EventCreatePage from './pages/admin/EventCreatePage';
import EventEditPage from './pages/admin/EventEditPage';
import OnsiteRegisterPage from './pages/admin/OnsiteRegisterPage';
import ReceiptStatusPage from './pages/admin/ReceiptStatusPage';
import NewChurchRegistrationPage from './pages/admin/NewChurchRegistrationPage';
import RenewChurchRegistrationPage from './pages/admin/RenewChurchRegistrationPage';
import BTManagePage from './pages/admin/BTManagePage';
import BTTeacherManagePage from './pages/admin/BTTeacherManagePage';
import BTAdminPage from './pages/admin/BTAdminPage';
import BTKeyManagePage from './pages/admin/BTKeyManagePage';
import BTAttendancePage from './pages/admin/BTAttendancePage';
import BQRegisterPage from './pages/BQRegisterPage';
import BQManagePage from './pages/admin/BQManagePage';
import BQRegistrationSummaryPrintPage from './pages/admin/BQRegistrationSummaryPrintPage';
import OlympicRegisterPage from './pages/OlympicRegisterPage';
import OlympicManagePage from './pages/admin/OlympicManagePage';
import PopupManagePage from './pages/admin/PopupManagePage';
import TeacherIdPage, { TeacherIdVerifyPage } from './pages/TeacherIdPage';
import BTTeacherHubPage from './features/btcert/admin/BTTeacherHubPage';
import CertificatePrintPage from './features/btcert/certificate/CertificatePrintPage';
import ChurchReportPortal from './features/btcert/church/ChurchReportPortal';
import { AttendGoPage, InstructorScreen } from './features/btcert/attend/AttendPages';
import RentalPortal from './features/rental/church/RentalPortal';
import RentalAdminPage from './features/rental/admin/RentalAdminPage';

// Theme wrapper component to handle theme switching
const ThemedApp: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const currentTheme = isAdminRoute ? adminTheme : mainTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Routes>
        {/* Public Routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<MainPage />} />
          <Route path="/events" element={<EventListPage />} />
          <Route path="/churches" element={<ChurchListPage />} />
          <Route path="/receipts" element={<ReceiptPage />} />
          <Route path="/bq-register/:eventId" element={<BQRegisterPage />} />
          <Route path="/bq-register/:eventId/:churchId" element={<BQRegisterPage />} />
          <Route path="/olympic-register/:eventId" element={<OlympicRegisterPage />} />
          <Route path="/olympic-register/:eventId/:churchId" element={<OlympicRegisterPage />} />
          <Route path="/rental" element={<RentalPortal />} />
        </Route>

        <Route path="/bt/teacher-id" element={<TeacherIdPage />} />
        <Route path="/bt/teacher-id/verify" element={<TeacherIdVerifyPage />} />

        {/* BT 완성보고서 교회 제출 · 수료증 인쇄 · 강의 출석 */}
        <Route path="/report" element={<ChurchReportPortal />} />
        <Route path="/report/print" element={<CertificatePrintPage source="church" />} />
        <Route path="/go" element={<AttendGoPage />} />
        <Route path="/go/screen/:sid" element={<InstructorScreen />} />

        {/* Admin Print Routes (no AdminLayout, so sidebar/header won't be printed) */}
        <Route
          path="/admin/bq/summary-print/:eventId"
          element={<AdminLoginGate>{() => <BQRegistrationSummaryPrintPage />}</AdminLoginGate>}
        />
        <Route
          path="/admin/bt-cert/print"
          element={<AdminLoginGate>{() => <CertificatePrintPage source="admin" />}</AdminLoginGate>}
        />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="events" element={<EventManagePage />} />
          <Route path="events/create" element={<EventCreatePage />} />
          <Route path="events/edit/:id" element={<EventEditPage />} />
          <Route path="churches/*" element={<ChurchManagePage />} />
          <Route path="new-registration" element={<NewChurchRegistrationPage />} />
          <Route path="renew-registration" element={<RenewChurchRegistrationPage />} />
          <Route path="receipts" element={<ReceiptManagePage />} />
          <Route path="receipts/events" element={<EventReceiptManagePage />} />
          <Route path="onsite" element={<OnsiteRegisterPage />} />
          <Route path="receipt-status" element={<ReceiptStatusPage />} />
          <Route path="bt" element={<BTAdminPage />} />
          <Route path="bt/keys/:churchManagerId" element={<BTKeyManagePage />} />
          <Route path="bt/attendance/:sessionId" element={<BTAttendancePage />} />
          <Route path="bt-manage" element={<BTManagePage />} />
          <Route path="bt-teachers" element={<BTTeacherHubPage />} />
          <Route path="bt-teachers/manage" element={<BTTeacherManagePage />} />
          <Route path="bt-teachers/:churchManagerId" element={<BTTeacherManagePage />} />
          <Route path="bq" element={<BQManagePage />} />
          <Route path="olympic" element={<OlympicManagePage />} />
          <Route path="popups" element={<PopupManagePage />} />
          <Route path="rental" element={<RentalAdminPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ThemedApp />
    </BrowserRouter>
  );
};

export default App;
